import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import {
  brandFromPathname,
  DEFAULT_REFERRAL_BRAND,
  REFERRAL_BRANDS,
  ReferralBrand,
  ReferralBrandConfig,
  referralBrandConfig,
}                                                                                   from './brand';
import { saveReferralBrand }                                                        from './referralBrandStorage';
import { resolveReferralBrand }                                                     from './resolveBrand';

interface ReferralBrandContextValue {
  brand: ReferralBrand;
  config: ReferralBrandConfig;
}

const ReferralBrandContext = createContext<ReferralBrandContextValue>({
  brand:  DEFAULT_REFERRAL_BRAND,
  config: REFERRAL_BRANDS[DEFAULT_REFERRAL_BRAND],
});

export function ReferralBrandProvider({ pathname, children }: PropsWithChildren<{ pathname: string }>) {
  const routeBrand = brandFromPathname(pathname);
  // Seeded synchronously so the first paint is already correctly branded.
  const [brand, setBrand] = useState<ReferralBrand>(() => resolveReferralBrand(pathname));

  // This provider is NOT remounted on every navigation, so the initialiser above runs once per
  // instance and cannot be relied on to re-brand. The router's <Switch> renders a single child,
  // and sibling routes that share a component type (the two landing pages are both PublicRoute,
  // and both pass layout={Secondary}) reconcile rather than remount. Verified with a mount
  // counter: navigating /energy/referral -> /communications/referral keeps one instance alive and
  // re-renders it with a new pathname. This effect is what re-brands it. Guarded on routeBrand so
  // the brand-less journey paths (/referral/:complaintId/...) keep whatever was resolved.
  useEffect(() => {
    if (routeBrand) setBrand(routeBrand);
  }, [routeBrand]);

  useEffect(() => {
    saveReferralBrand(brand);
  }, [brand]);

  // Memoised so consumers are not re-rendered by an identity change on every provider render.
  const value = useMemo(() => ({ brand, config: referralBrandConfig(brand) }), [brand]);

  return (
    <ReferralBrandContext.Provider value={value}>
      {children}
    </ReferralBrandContext.Provider>
  );
}

export const useReferralBrand = (): ReferralBrandContextValue => useContext(ReferralBrandContext);
